import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import {
  undoBulkOperation as undoBulkOperationApi,
  UndoBulkOperationResponse,
} from 'api/tasksList';
import {
  serializeUndoBulkOperationRequestBody,
  deserializeUndoBulkOperationResponse,
  trackUndoBulkOperationEvent,
} from './utils';
import { Status } from 'utils/customTypes';
import { SnackbarProps } from 'Hooks/useSnackbarNotifications/useSnackbarNotifications';
import {
  generateUndoBulkOperationNotifications,
  generateRejectedUndoBulkOperationNotifications,
  generateBulkActionInProgressNotifications,
} from './notifications';
import { SLICE_STATUS } from 'utils/constants';

interface BulkActionsState {
  status?: Status;
  error?: string;
  notifications?: Record<string, SnackbarProps>;
  bulkOperationInProgress?: boolean;
  bulkActionOperationId?: string;
  bulkActionOperationType?: 'delete' | 'update';
}

const initialState: BulkActionsState = {};

function undoBulkOperationCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().projectTasksList.bulkActions.value;
  return status !== SLICE_STATUS.LOADING;
}
interface UndoBulkOperationParams {
  projectId: string;
}

export const undoBulkOperation = createAsyncThunk<
  UndoBulkOperationResponse,
  UndoBulkOperationParams,
  ThunkApi
>(
  'projectTasksList/undoBulkOperation',
  async (params: UndoBulkOperationParams, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { bulkActionOperationId, bulkActionOperationType } =
        state.projectTasksList.bulkActions.value;

      if (!bulkActionOperationId) {
        return rejectWithValue(new Error('No bulk action operation id found'));
      }

      const requestBody = serializeUndoBulkOperationRequestBody(
        bulkActionOperationType!,
        bulkActionOperationId!
      );

      const response = await undoBulkOperationApi({
        projectId: params.projectId,
        requestBody,
      });
      const deserializedResponse = await deserializeUndoBulkOperationResponse(
        response
      );
      trackUndoBulkOperationEvent(
        state,
        deserializedResponse,
        bulkActionOperationId,
        bulkActionOperationType!,
        params.projectId
      );
      return deserializedResponse;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  {
    condition: undoBulkOperationCondition,
  }
);

const bulkActionsSlice = createSlice({
  name: 'projectTasksList/bulkActions',
  initialState,
  reducers: {
    setBulkActionsNotifications: (
      state,
      action: PayloadAction<Record<string, SnackbarProps>>
    ) => {
      state.notifications = action.payload;
    },
    setBulkActionOperationIdAndType: (
      state,
      action: PayloadAction<{ id: string; type: 'delete' | 'update' }>
    ) => {
      state.bulkActionOperationId = action.payload.id;
      state.bulkActionOperationType = action.payload.type;
    },
    setBulkOperationInProgress: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationInProgress = action.payload;
    },
    resetBulkActionsNotifications: (state) => {
      state.notifications = {};
    },
    removeBulkActionNotification: (state, action: { payload: string }) => {
      delete state.notifications?.[action.payload];
    },
    displayInProgressNotification: (state) => {
      if (state.bulkOperationInProgress) {
        const notifications = generateBulkActionInProgressNotifications();
        state.notifications = notifications;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(undoBulkOperation.pending, (state) => {
        state.bulkOperationInProgress = true;
        state.status = SLICE_STATUS.LOADING;
        state.notifications = {};
      })
      .addCase(undoBulkOperation.fulfilled, (state, action) => {
        state.bulkOperationInProgress = false;
        state.status = SLICE_STATUS.SUCCESS;
        state.error = undefined;
        if (!action.payload) return;
        const notifications = generateUndoBulkOperationNotifications(
          action.payload.failedCount
        );
        state.notifications = notifications;
        state.bulkActionOperationId = undefined;
        state.bulkActionOperationType = undefined;
      })
      .addCase(undoBulkOperation.rejected, (state, action) => {
        state.bulkOperationInProgress = false;
        state.status = SLICE_STATUS.FAILED;
        state.error = action.error.message;
        const notifications = generateRejectedUndoBulkOperationNotifications();
        state.notifications = notifications;
      });
  },
});

const selectBulkActionsState = (state: RootState) =>
  state.projectTasksList.bulkActions.value;

export const selectBulkActionsNotifications = createSelector(
  [selectBulkActionsState],
  (bulkActions: BulkActionsState) =>
    bulkActions.notifications ? Object.values(bulkActions.notifications) : []
);

export const selectBulkActionsNotificationsIds = createSelector(
  [selectBulkActionsNotifications],
  (notifications: SnackbarProps[]) =>
    notifications.map((notification) => notification.id)
);

export const selectBulkActionOperationId = createSelector(
  [selectBulkActionsState],
  (bulkActions: BulkActionsState) =>
    bulkActions.bulkActionOperationId ?? undefined
);

export const selectBulkActionOperationType = createSelector(
  [selectBulkActionsState],
  (bulkActions: BulkActionsState) =>
    bulkActions.bulkActionOperationType ?? undefined
);

const selectUndoBulkOperationStatus = createSelector(
  [selectBulkActionsState],
  (bulkActions: BulkActionsState) => bulkActions.status ?? undefined
);

export const selectIsUndoBulkOperationLoading = createSelector(
  [selectUndoBulkOperationStatus],
  (status: Status | undefined) => status === SLICE_STATUS.LOADING
);

export const selectIsBulkOperationInProgress = createSelector(
  [selectBulkActionsState],
  (bulkActions: BulkActionsState) =>
    bulkActions.bulkOperationInProgress ?? false
);

export const {
  resetBulkActionsNotifications,
  removeBulkActionNotification,
  displayInProgressNotification,
  setBulkActionsNotifications,
  setBulkActionOperationIdAndType,
  setBulkOperationInProgress,
} = bulkActionsSlice.actions;

export default bulkActionsSlice.reducer;
